import { Component, computed, inject, input, InputSignal, Signal, signal, WritableSignal, Input, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { handleImageError } from '../../shared/common/utils';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MAT_SELECT_SCROLL_STRATEGY, MatSelectModule } from '@angular/material/select';
import { ResolutionService } from '../../shared/resolution.service';
import { CommonModule } from '@angular/common';
import { EmptyMessageComponent } from '../../shared/empty-message/empty-message.component';
import { OrderService } from '../../shared/order.service';
import { finalize, tap } from 'rxjs';
import { SubscriptionsService } from '../../settings/account/subscriptions/subscriptions.service';
import { BundleEditionComponent } from './bundle-edition/bundle-edition.component';
import { UpdateOrderParams } from '../../shared/types/order.type';
import { ProductType } from '../../shared/types/common.enums';
import { FormsModule } from '@angular/forms';
import { toObservable } from '@angular/core/rxjs-interop';
import { NotificationService } from '../../shared/notification/notification.service';
import { setUpBundleChangesLog } from '../../shared/common/bundle.utils';
import { MatInputModule } from '@angular/material/input';
import { NumberRestrictionDirective } from '../../shared/directives/number-restriction.directive';
import { ScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { updateFirebaseOrderAndSubTrigger } from '../../shared/common/order.utils';
import { SignalsStoreService } from '../../shared/signals-store.service';
import { FirebaseCrudService } from '../../shared/firebase-crud.service';

export function scrollStrategyFactory(sso: ScrollStrategyOptions): () => ScrollStrategy {
  return () => sso.reposition();
}

@Component({
  selector: 'app-order-product',
  imports: [
    RouterModule,
    MatFormFieldModule,
    MatSelectModule,
    CommonModule,
    EmptyMessageComponent,
    BundleEditionComponent,
    FormsModule,
    MatInputModule,
    NumberRestrictionDirective
  ],
  providers: [
    { provide: MAT_SELECT_SCROLL_STRATEGY, useFactory: scrollStrategyFactory, deps: [ScrollStrategyOptions] }
  ],
  templateUrl: './order-product.component.html',
  styleUrl: './order-product.component.scss'
})
export class OrderProductComponent implements OnInit {

  //#region Injections
  private resolutionService = inject(ResolutionService);

  private orderService = inject(OrderService);

  private subscriptionsService = inject(SubscriptionsService);

  private notificationService = inject(NotificationService);

  private signalsStoreService = inject(SignalsStoreService);

  private firebaseCrudService = inject(FirebaseCrudService);
  //#endregion

  //#region Inputs
  product: InputSignal<any> = input.required();

  isSubscription: InputSignal<boolean> = input.required();
  @Input() accordionParentId?: string;
  //#endregion

  //#region Signals

  isValueHigherThan10: WritableSignal<boolean> = signal(false);

  showUpdateButton = computed(() => this.isValueHigherThan10() && (this.previousValue !== +(this.newQuantity() || 0)));

  isDeletingProduct: WritableSignal<boolean> = signal(false);

  //#endregion

  //#region Computed Properties

  isMobile: Signal<boolean> = computed(() => this.resolutionService.isMobile());

  order: Signal<any> = computed(() => this.orderService.order());

  orderId: Signal<number | null> = computed(() => this.order().id);

  totalPrice: Signal<number> = computed(() => this.setupProductTotalPrice());

  toggleId: Signal<string> = computed(() => `toggle_${this.product().id}_${this.isSubscription() ? 'subscription' : 'one-time'}`);

  //#endregion

  private product$ = toObservable(this.product);

  preOrderedProductDisclaimer = 'This pre-ordered product cannot be modified or removed.';

  handleImageError = handleImageError;

  isInputValid!: boolean;
  previousValue!: number;
  selectedQuantity: WritableSignal<any> = signal(1);
  newQuantity: WritableSignal<number> = signal(1);
  availableQuantities: Signal<{ val: number, name: string }[]> = computed(() => this.#setUpAvailableQuantities());

  ngOnInit(): void {
    this.product$.subscribe(product => {
      if (product) {
        this.selectedQuantity.set(product.quantity);
        this.newQuantity.set(product.quantity);
        this.previousValue = product.quantity;
        this.isValueHigherThan10.set(product.quantity >= 10);
      }
    });
  }

  onKeyUp(event: KeyboardEvent) {
    const value = +(event.target as HTMLInputElement).value;
    this.isValueHigherThan10.set(value >= 10);
  }

  onInputQuantityChange(event: Event) {
    const value = +(event.target as HTMLInputElement).value;
    if (!isNaN(value) && value > 0) {
      this.newQuantity.set(value);
    }
  }

  onQuantityChange(event: any) {
    const value = event.value;
    if (!value) return;

    this.newQuantity.set(value);

    if (value === 10) {
      this.isValueHigherThan10.set(true);
      return;
    }

    this.updateProduct(this.product());
  }

  validateQuantity() {
    if (!this.newQuantity())
      return this.notificationService.show({ text: 'Please enter a valid quantity', type: 'error' });

    this.updateProduct(this.product());
  }

  deleteProduct() {
    if (this.isDeletingProduct())
      return;

    this.isDeletingProduct.set(true);

    const orderId = this.orderId();
    if (!orderId) {
      this.isDeletingProduct.set(false);
      return;
    }

    this.orderService
      .deleteProductOrder(orderId, this.product().lineId)
      .pipe(
        finalize(() => {
          this.isDeletingProduct.set(false);
        })
      )
      .subscribe()
  }


  skipProduct() {
    this.subscriptionsService.update(this.product().subscription.id, { skip: true }, true).subscribe()
  }

  private setupProductTotalPrice() {
    const p = this.product();
    if (!p.isBundle) return p.totalPrice;

    const totalPrice = this.orderService.calculateProductTotalPrice(p);
    return totalPrice;
  }

  updateProduct(product: any) {
    const orderId = this.orderId();
    if (!orderId) return;

    const data: UpdateOrderParams = {
      orderId,
      product: {
        ...product,
        quantity: this.newQuantity(),
        bundleQuantityChanged: product.isBundle
      } as any,
      type: this.isSubscription() ? ProductType.subscription : ProductType.common,
      bundleChangesLog: this.product()?.isBundle ? setUpBundleChangesLog(this.product(), this.newQuantity()) : undefined,
    }

    this.orderService
      .updateOrder(data, this.isSubscription(), true).pipe(
        tap(() => updateFirebaseOrderAndSubTrigger(this.signalsStoreService, this.firebaseCrudService))
      ).subscribe();
  }

  #setUpAvailableQuantities(): { val: number, name: string }[] {
    const res = [] as { val: number, name: string }[];
    const stockInfo = this.product()?.stockInfo?.[0] || null;
    const productQuantity = this.product().quantity || 1;
    if (!stockInfo) return res;

    const stock = stockInfo.stock + (stockInfo.limit * -1)
    const totalAvailableStock = stock < 0 ? 0 : stock;

    let available = totalAvailableStock + productQuantity;
    if (available > 10) available = 10;

    if (this.product().isBundle)
      available = 10;

    if ((available) <= 10) {
      for (let index = 1; index <= available; index++) {
        res.push({ val: index, name: (index === 10 ? '+10' : index.toString()) });
      }
    }

    return res
  }

}
